import { type FeatureFlagKey, featureFlags } from './flags';
import {
  BETA_CHECK_COOKIE,
  BETA_ROLLOUT_EXPERIMENT,
  BETA_ROLLOUT_MARKER_COOKIE,
  BETA_ROLLOUT_OPT_OUT_COOKIE,
  HP_NEXT_COOKIE,
} from '../../middleware/rolloutCookies';

const COOKIE_PREFIX = 'ff_';
const ONE_YEAR_SECONDS = 31536000;

function clearCookie(name: string): void {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
}

function setCookie(name: string, value: string): void {
  document.cookie = `${name}=${value}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`;
}

function parseCookies(cookieString: string): Record<string, string> {
  const cookies: Record<string, string> = {};
  for (const pair of cookieString.split(';')) {
    const [rawKey, ...rest] = pair.split('=');
    const key = rawKey?.trim();
    if (key) {
      cookies[key] = rest.join('=').trim();
    }
  }
  return cookies;
}

export function getFlagValue(key: FeatureFlagKey, cookieString?: string): string | undefined {
  const raw = cookieString ?? (typeof document !== 'undefined' ? document.cookie : '');
  const cookies = parseCookies(raw);
  const cookieKey = `${COOKIE_PREFIX}${key}`;
  return cookies[cookieKey];
}

export function getAllFlagValues(
  cookieString?: string,
): Record<FeatureFlagKey, string | boolean> {
  const result = {} as Record<FeatureFlagKey, string | boolean>;

  for (const flag of featureFlags) {
    const raw = getFlagValue(flag.key as FeatureFlagKey, cookieString);

    if (flag.type === 'boolean') {
      result[flag.key as FeatureFlagKey] = raw !== undefined ? raw === '1' : flag.defaultValue;
    } else {
      result[flag.key as FeatureFlagKey] =
        raw !== undefined && flag.variants.includes(raw as never) ? raw : flag.defaultValue;
    }
  }

  return result;
}

export function setFeatureFlags(
  flags: Partial<Record<FeatureFlagKey, string | boolean>>,
): void {
  if (typeof document === 'undefined') return;

  for (const flag of featureFlags) {
    const key = flag.key as FeatureFlagKey;
    const value = flags[key];
    const cookieKey = `${COOKIE_PREFIX}${key}`;

    if (value === undefined) continue;

    const isDefault =
      flag.type === 'boolean'
        ? value === flag.defaultValue
        : value === flag.defaultValue;

    if (isDefault) {
      clearCookie(cookieKey);
    } else {
      const cookieValue = flag.type === 'boolean' ? (value ? '1' : '0') : String(value);
      setCookie(cookieKey, cookieValue);
    }
  }

  window.location.reload();
}

export function clearAllFeatureFlags(): void {
  if (typeof document === 'undefined') return;

  for (const flag of featureFlags) {
    const cookieKey = `${COOKIE_PREFIX}${flag.key}`;
    clearCookie(cookieKey);
  }
  // Clearing all flags also manually opts out of the HotPads Next beta. Keep
  // this aligned with middleware/featureFlags.ts: legacy needs the opt-out
  // marker after reload so ZEXP rollout enrollment does not immediately restore
  // the hp_next cookie.
  clearCookie(HP_NEXT_COOKIE);
  clearCookie(BETA_ROLLOUT_MARKER_COOKIE);
  clearCookie(BETA_CHECK_COOKIE);
  setCookie(BETA_ROLLOUT_OPT_OUT_COOKIE, BETA_ROLLOUT_EXPERIMENT);

  window.location.reload();
}
